import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { removeOrClearFill } from "../../ActionSummaryTableHelperFunctions";
import { toast } from "react-toastify";
import tradeDataStore from "@/store/tradeDataStore";
import { isFillCancelled, getFillFromStore } from "../fillStateHelpers";
import { trashBin } from "@/components/icons/SVGIcons";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { statusToRemoveFillButtons } from "./EditFillButtonCellRenderer";

/**
 * RemoveFillButtonCellRenderer
 *
 * Renders a "Remove" button in the Order Fills table.
 * When clicked:
 * - New fills: Removes completely (unless last fill, then clears values)
 * - Existing fills: Sets to "Cancel (Update Pending)" status
 *
 * Not shown for:
 * - Cancelled fills (already cancelled)
 * - OP trades (only RP can edit)
 */
const RemoveFillButtonCellRenderer = (params: any) => {
  // Subscribe to tradeData for reactive updates (hooks must run before any early return)
  const tradeData = tradeDataStore((state) => state.tradeData);

  if (!params.data) {
    return null;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;
  const isRP = fullTradeData?.role === "RP";
  const fillId = params.data.fillId;

  // Only show button for RP trades
  if (!isRP) {
    return null;
  }

  const currentTrade = tradeData.find((trade) => trade.esid === esid);
  const status = currentTrade?.status ?? fullTradeData?.status;

  // Hide at Filled/NoMoreFill/Affirmed (trade is complete / allowing the OP to finish - RP can't edit)
  // Show at ChangePending (RP is editing again) and Working
  if (
    status &&
    statusToRemoveFillButtons.includes(status as ExecutionStrategyStatus)
    // statusToRemoveFillButtons used in Edit as they are the same times to use.
  ) {
    return null;
  }

  const extFillId = params.data?.fillData?.extFillId;

  // Get the fill from the store to check its state
  // extFillId fallback: when fillId is stale after backend assigns new fillId
  const fill =
    esid && fillId !== undefined && blockId !== undefined
      ? getFillFromStore(tradeData, esid, blockId, fillId, extFillId)
      : null;

  // Hide button for cancelled fills
  const isCancelled = fill ? isFillCancelled(fill) : false;
  if (isCancelled) {
    return null;
  }

  const handleRemoveFill = () => {
    if (esid && blockId !== undefined && fillId !== undefined) {
      // Update the store and get the action taken
      const actionTaken = removeOrClearFill(esid, blockId, fillId);

      if (actionTaken === "removed" && params.api) {
        // Remove the row from the grid - required because AG Grid's detail row data
        // is only fetched once at expansion, so it doesn't know the fill was removed
        params.api.applyTransaction({ remove: [params.data] });
      } else if (actionTaken === "cleared") {
        toast.info("Fill value cleared.");
        // Cell renderers subscribe to Zustand and will re-render automatically
      } else if (actionTaken === "cancelled") {
        // toast.info("Fill marked for cancellation."); - No longer needed as the status changes.
        // Cell renderers subscribe to Zustand and will re-render automatically
      }
    }
  };

  return (
    <div
      data-testid="removeFillButtonCellContainer"
      className="flex h-full items-center justify-center"
    >
      <button
        onClick={handleRemoveFill}
        className="flex min-h-[20px] min-w-[20px] items-center justify-center rounded bg-gray-400/50 text-xs font-semibold text-gray-600 opacity-90 hover:bg-red-500/80 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:bg-gray-500/50 dark:text-white dark:hover:bg-red-600/80"
        title="Remove fill"
      >
        {trashBin}
      </button>
    </div>
  );
};

export default RemoveFillButtonCellRenderer;
